import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord } from '@deity-io/di-data-basics';
import { Box, Input } from '@deity-io/portal-ui';
import { CACHE_FIRST } from 'contracts/constants';
import type { FormikErrors } from 'formik';
import type { CSSProperties, FC } from 'react';
import { useGetEnvironmentByAccountQuery } from 'services/graphql/generated/hooks';

type EnvironmentVariableContainerProps = {
  values: IPropertyBag;
  variableKey: string;
  setFieldValue?: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<FormikErrors<IPropertyBag>> | Promise<void>;
  setFieldTouched?: (
    field: string,
    touched?: boolean,
    shouldValidate?: boolean | undefined
  ) => Promise<FormikErrors<IPropertyBag>> | Promise<void>;
  errors?: FormikErrors<IPropertyBag>;
  disabled?: boolean;
};
/**
 * Does not show settings with a _ prefix. this to hide the new name in
 */
const EnvironmentVariableContainer: FC<EnvironmentVariableContainerProps> = ({
  values,
  variableKey,
  setFieldValue,
  setFieldTouched,
  disabled = false,
  errors
}) => {
  const { data: environments } = useGetEnvironmentByAccountQuery({
    variables: { limit: 50, offset: 0 },
    ...CACHE_FIRST
  });

  const headerStyle: CSSProperties = {
    textAlign: 'left',
    color: '#919aa7',
    fontSize: '12px',
    paddingBottom: '4px'
  };

  const valueCellStyle: CSSProperties = {
    border: '1px solid #c4cbd3',
    padding: '0 4px',
    marginBottom: 0,
    display: 'flex',
    alignItems: 'flex-end'
  };

  const environmentCellStyle: CSSProperties = {
    border: '1px solid #c4cbd3',
    borderRight: 'none',
    padding: '0 10px',
    width: '200px',
    color: '#919aa7',
    backgroundColor: '#f3f5f7',
    fontSize: '14px'
  };

  const settings: Array<{
    key: string;
    value: string | number;
    valid: boolean;
    environment?: any;
  }> = environments
    ? environments.getEnvironmentByAccount.data.map(environment => ({
        key: environment.name,
        value: values[environment.name],
        valid: true,
        environment
      }))
    : [];

  mapRecord(values, (value, key) => {
    if (!settings.find(setting => setting.key === key)) {
      settings.push({
        key,
        value,
        valid: false
      });
    }

    return undefined;
  });

  return (
    <Box>
      <table style={{ width: '100%', borderSpacing: 0 }}>
        <thead key="thead">
          <tr key="rowHead">
            <th key="head_env" style={headerStyle}>
              Environment
            </th>
            <th key="value" style={headerStyle}>
              Value
            </th>
          </tr>
        </thead>
        <tbody key="tbody">
          {settings.map((setting, index) => {
            if (setting.key[0] === '_') {
              return null;
            }

            return (
              <tr key={setting.key}>
                <td
                  key="settingKey"
                  style={{
                    ...environmentCellStyle,
                    ...(index !== settings.length - 1 ? { borderBottom: 'none' } : {}),
                    ...(index === 0 ? { borderRadius: '4px 0 0 0' } : {}),
                    ...(index === settings.length - 1 ? { borderRadius: '0 0 0 4px' } : {})
                  }}
                >
                  <div>{setting.environment ? `${setting.environment.description}` : setting.key}</div>
                </td>
                <td
                  key="settingInput"
                  style={{
                    ...valueCellStyle,
                    ...(index !== settings.length - 1 ? { borderBottom: 'none' } : {}),
                    ...(index === 0 ? { borderRadius: '0 4px 0 0' } : {}),
                    ...(index === settings.length - 1 ? { borderRadius: '0 0 4px 0' } : {})
                  }}
                >
                  <Input
                    key={`${variableKey}_${setting.key}`}
                    fullWidth
                    disabled={disabled}
                    variant="standard"
                    placeholder="value"
                    value={setting.value}
                    onBlur={() => {
                      if (setFieldTouched) {
                        setFieldTouched(setting.key, true);
                      }
                    }}
                    onChange={e => {
                      if (setFieldValue) {
                        setFieldValue(setting.key, e.target.value);
                      }
                    }}
                    // error={!!errors?.dev}
                    helperText={errors?.dev}
                    sx={{
                      mb: '-1px',
                      px: 1,
                      '& .MuiInput-underline:before': { borderBottom: 'none' },
                      '& .Mui-disabled:before': { borderBottom: 'none !important' }
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
};

export default EnvironmentVariableContainer;
